import { __assign, __read } from "tslib";
import React, { useMemo, useState } from "react";
import FormField from "@adsk/alloy-react-form-field";
import TextInput from "@adsk/alloy-react-text-input";
import {
  AutocompleteControl,
  Control,
  SingleSelectMenu,
  useAutocomplete,
  useDropdown,
} from "@adsk/alloy-react-dropdown";
import { useTranslations } from "../../utils";
import { SubmitError, TemplateCreateType } from "../../shared/types";
import {
  AnalyticsEvents,
  MAX_PROJECTS_TO_DISPLAY,
} from "../../shared/constants";
import { FormFieldSpacer } from "../../shared/SharedDataInputModal.styles";
export var ProjectTemplateCreateDataInputModal = function (_a) {
  var accountsLoading = _a.accountsLoading,
    projectsLoading = _a.projectsLoading,
    accounts = _a.accounts,
    isSubmitting = _a.isSubmitting,
    name = _a.name,
    onNameChange = _a.onNameChange,
    onSelectAccount = _a.onSelectAccount,
    onSelectProject = _a.onSelectProject,
    projects = _a.projects,
    selectedAccountId = _a.selectedAccountId,
    templateCreateType = _a.templateCreateType,
    inputError = _a.inputError,
    track = _a.track;
  // Configure translations
  var T = useTranslations().T;
  // Configure error message
  var inputErrorMessage;
  switch (inputError) {
    case SubmitError.DuplicateName:
      inputErrorMessage = T.translate("errors.duplicateName");
      break;
    case SubmitError.InvalidName:
      inputErrorMessage = T.translate("errors.invalidName");
      break;
    case SubmitError.NameTooLong:
      inputErrorMessage = T.translate("errors.nameTooLong");
      break;
    case SubmitError.Other:
      inputErrorMessage = T.translate("errors.other");
      break;
  }
  // Only display projects from the selected account
  var projectsToDisplay = useMemo(
    function () {
      if (selectedAccountId) {
        return projects.filter(function (project) {
          return project.accountId === selectedAccountId;
        });
      } else {
        return projects;
      }
    },
    [selectedAccountId, projects]
  );
  var projectItems = projectsToDisplay.map(function (project) {
    return {
      label: project.name,
      value: project.id,
    };
  });
  var _b = __read(useState(null), 2),
    selectedProjectItem = _b[0],
    setSelectedProjectItem = _b[1];
  // Configure the accounts dropdown
  var AccountsDropdown = function (props) {
    var accountItems = props.accounts.map(function (account) {
      return {
        label: account.name,
        value: account.id,
      };
    });
    var _a = useDropdown({
        items: accountItems,
        selectedItem: accountItems.find(function (account) {
          return account.value === selectedAccountId;
        }),
        onSelectedItemChange: function (_a) {
          var selectedItem = _a.selectedItem;
          selectedItem && onSelectAccount(selectedItem.value);
          setSelectedProjectItem(null);
          track && track(AnalyticsEvents.createTemplateModal.selectAccount);
        },
      }),
      controlProps = _a.controlProps,
      menuProps = _a.menuProps;
    return React.createElement(
      FormFieldSpacer,
      null,
      React.createElement(
        FormField,
        {
          "data-testid": "CreateTemplateModal.AccountDropdown.Label",
          label: T.translate("genericStrings.account"),
          required: true,
          labelVariant: FormField.LABEL_VARIANTS.TOP,
        },
        React.createElement(
          Control,
          __assign({}, controlProps, {
            "data-testid": "CreateTemplateModal.AccountDropdown.Toggle",
            disabled: isSubmitting,
            isLoading: props.loading,
          })
        ),
        React.createElement(
          SingleSelectMenu,
          __assign(
            { "data-testid": "CreateTemplateModal.AccountDropdown.Input" },
            menuProps
          )
        )
      )
    );
  };
  // Configure the projects dropdown
  var ProjectsDropdown = function (props) {
    var _a = useAutocomplete({
        items: props.items,
        selectedItem: props.selectedItem,
        onSelectedItemChange: function (_a) {
          var selectedItem = _a.selectedItem;
          if (selectedItem) {
            // update the selectedItem within the AutocompleteControl
            setSelectedProjectItem(selectedItem);
            // update the selectedProjectId in the CreateTemplateModal so the Submit
            // button is properly enabled
            onSelectProject(selectedItem.value);
          }
          track && track(AnalyticsEvents.createTemplateModal.selectProject);
        },
      }),
      controlProps = _a.controlProps,
      menuProps = _a.menuProps;
    var noProjects = !!props.selectedAccountId && !!!props.items.length;
    var noProjectsError =
      !props.loading && noProjects
        ? T.translate("errors.noProjectsAvailable")
        : undefined;
    var menuItems = menuProps.menuItems;
    return React.createElement(
      FormFieldSpacer,
      null,
      React.createElement(
        FormField,
        {
          "data-testid": "CreateTemplateModal.ProjectDropdown.Label",
          error: noProjectsError,
          errorVariant: FormField.ERROR_VARIANTS.CAPTION,
          label: T.translate("genericStrings.project"),
          required: true,
          labelVariant: FormField.LABEL_VARIANTS.TOP,
        },
        React.createElement(
          AutocompleteControl,
          __assign({}, controlProps, {
            "data-testid": "CreateTemplateModal.ProjectDropdown.Toggle",
            disabled: isSubmitting || noProjects || !props.selectedAccountId,
            isLoading: props.loading,
          })
        ),
        React.createElement(
          SingleSelectMenu,
          __assign({}, menuProps, {
            "data-testid": "CreateTemplateModal.ProjectDropdown.Input",
            // only display the first N items in the dropdown menu - if the user enters
            // in a search string, it will first perform the search and then narrow down the
            // list
            menuItems: menuItems.slice(0, MAX_PROJECTS_TO_DISPLAY),
          })
        )
      )
    );
  };
  return React.createElement(
    "div",
    { "data-testid": "CreateTemplateModal.DataInput" },
    React.createElement(
      FormField,
      {
        "data-testid": "CreateTemplateModal.TemplateName.Label",
        error: inputErrorMessage || undefined,
        errorVariant: FormField.ERROR_VARIANTS.CAPTION,
        label: T.translate("createTemplateModal.templateNameLabel"),
        required: true,
        labelVariant: FormField.LABEL_VARIANTS.TOP,
      },
      React.createElement(TextInput, {
        "data-testid": "CreateTemplateModal.TemplateName.Input",
        value: name,
        onChange: function (e) {
          return onNameChange(e.target.value);
        },
        disabled: isSubmitting,
      })
    ),
    (accountsLoading || accounts.length > 1) &&
      React.createElement(AccountsDropdown, {
        accounts: accounts,
        loading: accountsLoading,
      }),
    templateCreateType === TemplateCreateType.FromProject &&
      React.createElement(ProjectsDropdown, {
        items: projectItems,
        selectedItem: selectedProjectItem,
        loading: projectsLoading,
        selectedAccountId: selectedAccountId,
      })
  );
};
